import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import useGA from './hooks/useGA';
import axios from 'axios';
import './i18n';   // Bootstraps i18next before any component renders.
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import Onboarding from './pages/Onboarding';
import EnhancedOnboarding from './pages/EnhancedOnboarding';
import OnboardingFlow from './pages/OnboardingFlow';
import UserPortal from './pages/UserPortal';
import FreezeDryDatabase from './pages/FreezeDryDatabase';
import AIChat from './pages/AIChat';
import RecipeBuilder from './pages/RecipeBuilder';
import BatchAnalyzer from './pages/BatchAnalyzer';
import ProductionForecast from './pages/ProductionForecast';
import WasteCostOptimizer from './pages/WasteCostOptimizer';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import BusinessCalc from './pages/BusinessCalc';
import Calculators from './pages/Calculators';
import HouseholdCalculator from './pages/HouseholdCalculator';
import BatchTracker from './pages/BatchTracker';
import LabelMakerPage from './pages/LabelMakerPage';
import FoodPrepResourcePage from './pages/FoodPrepResourcePage';
import RecipeCenter from './pages/RecipeCenter';
import BatchHistory from './pages/BatchHistory';
import LearningCatalog from './pages/LearningCatalog';
import CourseDetail from './pages/CourseDetail';
import LessonViewer from './pages/LessonViewer';
import InstructorProfile from './pages/InstructorProfile';
import { BlogList, BlogPost } from './pages/Blog';
import TopicPage from './pages/TopicPage';
import AdminBlogEditor from './pages/admin/AdminBlogEditor';
import VideoLibrary from './pages/VideoLibrary';
import Forum from './pages/Forum';
import ForumThread from './pages/ForumThread';
import NotificationsPage from './pages/NotificationsPage';
import AnalyticsDashboard from './pages/admin/AnalyticsDashboard';
import UserManagement from './pages/admin/UserManagement';
import AdminAssignments from './pages/admin/AdminAssignments';
import AdminCreditPacks from './pages/admin/AdminCreditPacks';
import AdminCreditPurchases from './pages/admin/AdminCreditPurchases';
import AdminStoreWaitlist from './pages/admin/AdminStoreWaitlist';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminInvitesManager from './pages/admin/AdminInvitesManager';
import InviteAccept from './pages/InviteAccept';
import ModRoom from './pages/admin/ModRoom';
import ModeratorPortal from './pages/ModeratorPortal';
import AIGateHits from './pages/admin/AIGateHits';
import StudyBuddyAdmin from './pages/admin/StudyBuddyAdmin';
import AIModeration from './pages/admin/AIModeration';
import Messages from './pages/Messages';
import LiveSessions from './pages/LiveSessions';
import LiveSessionDetail from './pages/LiveSessionDetail';
import StudyRooms from './pages/StudyRooms';
import LiveSessionRoom from './pages/LiveSessionRoom';
import HelpCenter from './pages/HelpCenter';
import TermsOfUse from './pages/TermsOfUse';
import CommunityStandards from './pages/CommunityStandards';
import AdSlotManager from './pages/admin/AdSlotManager';
import AdSlotReport from './pages/admin/AdSlotReport';
import TutorialLearning from './pages/tutorials/TutorialLearning';
import TutorialCommunity from './pages/tutorials/TutorialCommunity';
import TutorialFreedrIQ from './pages/tutorials/TutorialFreedrIQ';
import TutorialTools from './pages/tutorials/TutorialTools';
import Partners from './pages/Partners';
import Store from './pages/Store';
import StoreWaitlist from './pages/StoreWaitlist';
import { useFeatureFlag } from './components/FeatureFlagsContext';
import Consulting from './pages/Consulting';
import AdminDashboard from './pages/AdminDashboard';
import AdminPortal from './pages/AdminPortal';
import LMSAdmin from './pages/admin/LMSAdmin';
import RefreshRadar from './pages/admin/RefreshRadar';
import CohortProgress from './pages/admin/CohortProgress';
import OverlapScan from './pages/admin/OverlapScan';
import CourseWorkspace from './pages/admin/CourseWorkspace';
import InfluencerDashboard from './pages/InfluencerDashboard';
import AdminInfluencersPage from './pages/admin/AdminInfluencersPage';
import AdminInfluencerEarnings from './pages/admin/AdminInfluencerEarnings';
import AdminFreedrIQResponses from './pages/admin/AdminFreedrIQResponses';
import AdminUserInsights from './pages/admin/AdminUserInsights';
import AdminToolWorkstation from './pages/admin/AdminToolWorkstation';
import Pricing from './pages/Pricing';
import WhyUs from './pages/WhyUs';
import Shop from './pages/Shop';
import CheckoutSuccess from './pages/CheckoutSuccess';
import CertificateView from './pages/CertificateView';
import MyCertificates from './pages/MyCertificates';
import Graduates from './pages/Graduates';
import Community from './pages/Community';
import CommunityCategory from './pages/CommunityCategory';
import MemberProfile from './pages/MemberProfile';
import FreedrIQ from './pages/FreedrIQ';
import FreedrIQChallenge from './pages/FreedrIQChallenge';
import FreedrIQChallengeAdmin from './pages/admin/FreedrIQChallengeAdmin';
import FreedrIQRankings from './pages/FreedrIQRankings';
import FreedrIQGrowth from './pages/FreedrIQGrowth';
import RedeemCode from './pages/RedeemCode';
import AdminCodesManager from './pages/admin/AdminCodesManager';
import AdminReferralAnalytics from './pages/admin/AdminReferralAnalytics';
import AdminReports from './pages/admin/AdminReports';
import AdminBugReports from './pages/admin/AdminBugReports';
import AdminCopyAttempts from './pages/admin/AdminCopyAttempts';
import AdminRefundRequests from './pages/admin/AdminRefundRequests';
import AdminLeakTraces from './pages/admin/AdminLeakTraces';
import LeakTraceResolver from './pages/LeakTraceResolver';
import TeamConsole from './pages/TeamConsole';
import TeamJoin from './pages/TeamJoin';
import FloatingUtilityDock from './components/utility/FloatingUtilityDock';
import MyNetwork from './pages/MyNetwork';
import CommunityTopic from './pages/CommunityTopic';
import CommunitySearch from './pages/CommunitySearch';
import CourseworkTranscript from './pages/CourseworkTranscript';
import AdminDirectory from './pages/admin/AdminDirectory';
import Directory from './pages/Directory';
import AdminBroadcast from './pages/admin/AdminBroadcast';
import WhatsNewAdmin from './pages/admin/WhatsNewAdmin';
import FeatureFlagsAdmin from './pages/admin/FeatureFlagsAdmin';
import AdminEvents from './pages/admin/AdminEvents';
import AdminCertificates from './pages/admin/AdminCertificates';
import PlatformHubPage from './pages/platform/PlatformHubPage';
import LearningPage from './pages/platform/LearningPage';
import AIAssistantPage from './pages/platform/AIAssistantPage';
import BusinessToolsPage from './pages/platform/BusinessToolsPage';
import CalculatorsPage from './pages/platform/CalculatorsPage';
import VideoLibraryPage from './pages/platform/VideoLibraryPage';
import CommunityPage from './pages/platform/CommunityPage';
import EventsCalendar from './pages/EventsCalendar';
import DesignSystemProvider from './components/DesignSystemProvider';
import PWAInstallButton from './components/PWAInstallButton';
import { CommandPaletteProvider } from './components/CommandPalette';
import { AdProvider } from './components/AdContext';
import { FeatureFlagsProvider, FeatureFlagsContext } from './components/FeatureFlagsContext';
import { CartProvider } from './context/CartContext';
import CartDrawer from './components/CartDrawer';
import ScrollToTop from './components/ScrollToTop';
import AdminGlobalBackButton from './components/admin/AdminGlobalBackButton';
import CookieConsent from './components/CookieConsent';
import TermsGate from './components/TermsGate';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Use same domain as frontend for API calls (Kubernetes ingress routes /api to backend)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

// Runtime-flag-gated Directory route. Reads the same FeatureFlagsContext
// the rest of the app uses; while the flag load is in flight we render
// a lightweight redirect (defaults=off) so the /directory URL never
// flashes stale content on first paint.
const DirectoryRoute = ({ user }) => {
  const { flags, ready } = React.useContext(FeatureFlagsContext);
  if (ready && flags?.directory_v2) return <Directory />;
  return <Navigate to={user ? '/dashboard' : '/'} replace />;
};

// Wraps useGA so we can call the hook inside <BrowserRouter> (which is
// where react-router's location context lives). Renders nothing.
function GATracker() { useGA(); return null; }


function App() {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Note: the "ResizeObserver loop completed with undelivered
  // notifications" swallow now lives in src/index.js so it wins the
  // race against CRA's react-error-overlay listener at module load.

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setIsAuthenticated(true);
      return response.data;
    } catch (error) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setIsAuthenticated(false);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Public alias — called from anywhere that mutates the user server-side
  // (access-code redeem, subscription checkout success, profile edit) so
  // the AuthContext user picks up new role / access_expires_at without a
  // full page reload.
  const refreshUser = fetchUser;

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
  };

  // Small gate component so the /store route can consult the runtime
  // feature flag (store_v1) without pulling context into main App().
  // Store hidden by default until the catalog is production-ready;
  // when off the route swaps to a waitlist landing so we don't lose
  // intent from visitors who followed a shared link.
  // eslint-disable-next-line react/no-multi-comp
  const StoreGate = ({ user: u }) => {
    const enabled = useFeatureFlag('store_v1');
    if (!u) return <Navigate to="/" replace />;
    if (!enabled) return <StoreWaitlist />;
    return <Store />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-craft-cream" data-testid="app-loading">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
          <div className="font-heading text-2xl text-foreground/80">Loading…</div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, isAuthenticated, setIsAuthenticated, login, logout, refreshUser, API }}>
      <HelmetProvider>
      <DesignSystemProvider>
        <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <GATracker />
          <AdminGlobalBackButton user={user} />
          <FeatureFlagsProvider>
          <CommandPaletteProvider>
          <AdProvider>
          <Routes>
            <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
            <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/onboarding" element={user ? <Onboarding /> : <Navigate to="/" />} />
            <Route path="/onboarding/new" element={user ? <EnhancedOnboarding /> : <Navigate to="/" />} />
            <Route path="/onboarding/survey" element={user ? <OnboardingFlow /> : <Navigate to="/" />} />
            <Route path="/portal" element={user ? <UserPortal /> : <Navigate to="/" />} />
            <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/" />} />
            <Route path="/database" element={user ? <FreezeDryDatabase /> : <Navigate to="/" />} />
            <Route path="/ai-chat" element={user ? <AIChat /> : <Navigate to="/" />} />
            <Route path="/recipes" element={user ? <RecipeBuilder /> : <Navigate to="/" />} />
            <Route path="/tools/batch-analyzer" element={user ? <BatchAnalyzer /> : <Navigate to="/" />} />
            <Route path="/tools/production-forecast" element={user ? <ProductionForecast /> : <Navigate to="/" />} />
            <Route path="/tools/waste-cost-optimizer" element={user ? <WasteCostOptimizer /> : <Navigate to="/" />} />
            <Route path="/calculators" element={user ? <Calculators /> : <Navigate to="/" />} />
            <Route path="/business" element={user ? <BusinessCalc /> : <Navigate to="/" />} />
            <Route path="/household-calculator" element={user ? <HouseholdCalculator /> : <Navigate to="/" />} />
            <Route path="/batch-tracker" element={user ? <BatchTracker /> : <Navigate to="/" />} />
            <Route path="/label-maker" element={user ? <LabelMakerPage /> : <Navigate to="/" />} />
            <Route path="/food-prep-resource" element={user ? <FoodPrepResourcePage /> : <Navigate to="/" />} />
            <Route path="/recipe-center" element={user ? <RecipeCenter /> : <Navigate to="/" />} />
            <Route path="/tools/recipe-center" element={user ? <RecipeCenter /> : <Navigate to="/" />} />
            <Route path="/community/recipes" element={user ? <RecipeCenter /> : <Navigate to="/" />} />
            <Route path="/batch-history" element={user ? <BatchHistory /> : <Navigate to="/" />} />
            <Route path="/learning" element={user ? <LearningCatalog /> : <Navigate to="/" />} />
            <Route path="/course/:courseId" element={user ? <CourseDetail /> : <Navigate to="/" />} />
            <Route path="/lesson/:lessonId" element={user ? <LessonViewer /> : <Navigate to="/" />} />
            <Route path="/instructor/:instructorId" element={user ? <InstructorProfile /> : <Navigate to="/" />} />
            {/* Blog — public, indexable by Google, no auth wall */}
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            {/* SEO topic hubs — public, indexable, aggregate courses + posts */}
            <Route path="/topics/:slug" element={<TopicPage />} />
            {/* Admin blog composer — auth-gated */}
            <Route path="/admin/blog" element={user?.role === 'admin' ? <AdminBlogEditor /> : <Navigate to="/" />} />
            <Route path="/videos" element={user ? <VideoLibrary /> : <Navigate to="/" />} />
            <Route path="/forum" element={user ? <Forum /> : <Navigate to="/" />} />
            <Route path="/forum/:threadId" element={user ? <ForumThread /> : <Navigate to="/" />} />
            <Route path="/notifications" element={user ? <NotificationsPage /> : <Navigate to="/" />} />

            {/* Public /platform marketing + SEO pages */}
            <Route path="/platform" element={<PlatformHubPage />} />
            <Route path="/platform/learning" element={<LearningPage />} />
            <Route path="/platform/ai-assistant" element={<AIAssistantPage />} />
            <Route path="/platform/business-tools" element={<BusinessToolsPage />} />
            <Route path="/platform/calculators" element={<CalculatorsPage />} />
            <Route path="/platform/videos" element={<VideoLibraryPage />} />
            <Route path="/platform/community" element={<CommunityPage />} />
            {/* Directory temporarily disabled — public marketing page routes back to All Tools. */}
            <Route path="/platform/directory" element={<Navigate to="/platform" replace />} />
            <Route path="/events" element={<EventsCalendar />} />
            <Route path="/admin/analytics" element={user?.role === 'admin' ? <AnalyticsDashboard /> : <Navigate to="/" />} />
            <Route path="/admin/users" element={(user?.role === 'admin' || user?.role === 'moderator') ? <UserManagement /> : <Navigate to="/" />} />
            <Route path="/admin/users/:userId" element={(user?.role === 'admin' || user?.role === 'moderator') ? <AdminUserDetail /> : <Navigate to="/" />} />
            <Route path="/admin/invites" element={user?.role === 'admin' ? <AdminInvitesManager /> : <Navigate to="/" />} />
            <Route path="/admin/mod-room" element={(user?.role === 'admin' || user?.role === 'moderator') ? <ModRoom /> : <Navigate to="/" />} />
            <Route path="/invite/:token" element={<InviteAccept />} />
            <Route path="/admin/ai-gate-hits" element={user?.role === 'admin' ? <AIGateHits /> : <Navigate to="/" />} />
            <Route path="/admin/study-buddy" element={user?.role === 'admin' ? <StudyBuddyAdmin /> : <Navigate to="/" />} />
            <Route path="/admin/moderation" element={user?.role === 'admin' ? <AIModeration /> : <Navigate to="/" />} />
            <Route path="/messages" element={user ? <Messages /> : <Navigate to="/login" />} />
            <Route path="/messages/:conversationId" element={user ? <Messages /> : <Navigate to="/login" />} />
            <Route path="/live" element={user ? <LiveSessions /> : <Navigate to="/login" />} />
            <Route path="/live/:sessionId" element={user ? <LiveSessionDetail /> : <Navigate to="/login" />} />
            <Route path="/live/:sessionId/room" element={user ? <LiveSessionRoom /> : <Navigate to="/login" />} />
            <Route path="/study" element={user ? <StudyRooms /> : <Navigate to="/login" />} />
            <Route path="/u/:handle" element={user ? <MemberProfile /> : <Navigate to="/login" />} />
            <Route path="/network" element={user ? <MyNetwork /> : <Navigate to="/login" />} />
            <Route path="/freedriq" element={user ? <FreedrIQ /> : <Navigate to="/login" />} />
            <Route path="/freedriq/challenge" element={user ? <FreedrIQChallenge /> : <Navigate to="/login" />} />
            <Route path="/freedriq/rankings" element={<FreedrIQRankings />} />
            <Route path="/freedriq/growth/:tier" element={user ? <FreedrIQGrowth /> : <Navigate to="/login" />} />
            <Route path="/redeem" element={user ? <RedeemCode /> : <Navigate to="/login" />} />
            <Route path="/admin/codes" element={user?.role === 'admin' ? <AdminCodesManager /> : <Navigate to="/dashboard" />} />
            <Route path="/admin/referrals" element={user?.role === 'admin' ? <AdminReferralAnalytics /> : <Navigate to="/dashboard" />} />
            <Route path="/admin/reports" element={(user?.role === 'admin' || user?.role === 'moderator') ? <AdminReports /> : <Navigate to="/dashboard" />} />
            <Route path="/admin/bug-reports" element={(user?.role === 'admin' || user?.role === 'moderator') ? <AdminBugReports /> : <Navigate to="/dashboard" />} />
            <Route path="/admin/content-protection" element={(user?.role === 'admin' || user?.role === 'moderator') ? <AdminCopyAttempts /> : <Navigate to="/dashboard" />} />
            <Route path="/admin/refund-requests" element={user?.role === 'admin' ? <AdminRefundRequests /> : <Navigate to="/dashboard" />} />
            <Route path="/admin/leak-traces" element={(user?.role === 'admin' || user?.role === 'moderator') ? <AdminLeakTraces /> : <Navigate to="/dashboard" />} />
            <Route path="/admin/freedriq-challenge" element={user?.role === 'admin' ? <FreedrIQChallengeAdmin /> : <Navigate to="/dashboard" />} />
            <Route path="/tutorial/learning" element={<TutorialLearning />} />
            <Route path="/tutorial/community" element={<TutorialCommunity />} />
            <Route path="/tutorial/freedriq" element={<TutorialFreedrIQ />} />
            <Route path="/tutorial/tools" element={<TutorialTools />} />
            <Route path="/help" element={user ? <HelpCenter /> : <Navigate to="/login" />} />
            <Route path="/terms" element={<TermsOfUse />} />
            <Route path="/leak/:token" element={<LeakTraceResolver />} />
            <Route path="/team" element={user ? <TeamConsole /> : <Navigate to="/login" />} />
            <Route path="/team/join/:token" element={<TeamJoin />} />
            <Route path="/community-standards" element={<CommunityStandards />} />
            <Route path="/admin/ad-slots" element={user?.role === 'admin' ? <AdSlotManager /> : <Navigate to="/dashboard" />} />
            <Route path="/admin/ad-slots/report/:placement" element={user?.role === 'admin' ? <AdSlotReport /> : <Navigate to="/dashboard" />} />
            <Route path="/partners" element={user ? <Partners /> : <Navigate to="/" />} />
            <Route path="/store" element={<StoreGate user={user} />} />
            <Route path="/consulting" element={user ? <Consulting /> : <Navigate to="/" />} />
            <Route path="/admin" element={user?.role === 'admin' ? <AdminDashboard /> : <Navigate to="/dashboard" />} />
            <Route path="/moderator" element={(user?.role === 'admin' || user?.role === 'moderator') ? <ModeratorPortal /> : <Navigate to="/dashboard" />} />
            <Route path="/admin/portal" element={user?.role === 'admin' ? <AdminPortal /> : <Navigate to="/dashboard" />} />
            <Route path="/admin/lms" element={user?.role === 'admin' ? <LMSAdmin /> : <Navigate to="/dashboard" />} />
            <Route path="/admin/lms/refresh-radar" element={user?.role === 'admin' ? <RefreshRadar /> : <Navigate to="/dashboard" />} />
            <Route path="/admin/lms/cohort-progress" element={user?.role === 'admin' ? <CohortProgress /> : <Navigate to="/dashboard" />} />
            <Route path="/admin/lms/overlap-scan" element={user?.role === 'admin' ? <OverlapScan /> : <Navigate to="/dashboard" />} />
 <Route path="/admin/lms/course/:courseId/workspace" element={user?.role === 'admin' ? <CourseWorkspace /> : <Navigate to="/dashboard" />} />
 <Route path="/influencer" element={user && (user.role === 'influencer' || user.role === 'admin') ? <InfluencerDashboard /> : <Navigate to="/dashboard" />} />
 <Route path="/admin/influencers" element={user?.role === 'admin' ? <AdminInfluencersPage /> : <Navigate to="/dashboard" />} />
 <Route path="/admin/influencer-earnings" element={user?.role === 'admin' ? <AdminInfluencerEarnings /> : <Navigate to="/dashboard" />} />
 <Route path="/admin/freedriq-responses" element={user?.role === 'admin' ? <AdminFreedrIQResponses /> : <Navigate to="/dashboard" />} />
 <Route path="/admin/user-insights" element={user?.role === 'admin' ? <AdminUserInsights /> : <Navigate to="/dashboard" />} />
            <Route path="/admin/workstation" element={user?.role === 'admin' ? <AdminToolWorkstation /> : <Navigate to="/dashboard" />} />
            <Route path="/pricing" element={<Pricing />} />
 <Route path="/why-us" element={<WhyUs />} />
 <Route path="/shop" element={<Shop />} />
 <Route path="/checkout/success" element={<CheckoutSuccess />} />
 <Route path="/certificate/course/:course_id" element={<CertificateView />} />
 <Route path="/certificate/:cert_number" element={<CertificateView />} />
 <Route path="/my-certificates" element={user ? <MyCertificates /> : <Navigate to="/login" />} />
 <Route path="/graduates" element={<Graduates />} />
 <Route path="/community" element={user ? <Community /> : <Navigate to="/" />} />
 <Route path="/community/search" element={user ? <CommunitySearch /> : <Navigate to="/" />} />
 <Route path="/community/c/:slug" element={user ? <CommunityCategory /> : <Navigate to="/" />} />
 <Route path="/community/t/:topic_id" element={user ? <CommunityTopic /> : <Navigate to="/" />} />
 <Route path="/coursework" element={user ? <CourseworkTranscript /> : <Navigate to="/login" />} />
            {/* Directory feature is disabled while we rework it. Guarded
                behind the runtime `directory_v2` feature flag so an admin
                can flip it back on from `/admin/feature-flags` without
                shipping a new build. When the flag is off, users get
                bounced to their appropriate home. Admins still see the
                AdminDirectory editor so unpublished data stays reachable. */}
            <Route path="/directory" element={<DirectoryRoute user={user} />} />
            <Route path="/admin/directory" element={user?.role === 'admin' ? <AdminDirectory /> : <Navigate to="/" />} />
            <Route path="/admin/broadcast" element={user?.role === 'admin' ? <AdminBroadcast /> : <Navigate to="/" />} />
            <Route path="/admin/whats-new" element={user?.role === 'admin' ? <WhatsNewAdmin /> : <Navigate to="/" />} />
            <Route path="/admin/feature-flags" element={user?.role === 'admin' ? <FeatureFlagsAdmin /> : <Navigate to="/" />} />
            <Route path="/admin/events" element={user?.role === 'admin' ? <AdminEvents /> : <Navigate to="/" />} />
            <Route path="/admin/certificates" element={user?.role === 'admin' ? <AdminCertificates /> : <Navigate to="/" />} />
            <Route path="/admin/assignments" element={user?.role === 'admin' ? <AdminAssignments /> : <Navigate to="/" />} />
            <Route path="/admin/credit-packs" element={user?.role === 'admin' ? <AdminCreditPacks /> : <Navigate to="/" />} />
            <Route path="/admin/credit-purchases" element={user?.role === 'admin' ? <AdminCreditPurchases /> : <Navigate to="/" />} />
            <Route path="/admin/store-waitlist" element={user?.role === 'admin' ? <AdminStoreWaitlist /> : <Navigate to="/" />} />
          </Routes>
          <Toaster position="bottom-left" richColors offset={20} />
          <PWAInstallButton />
          <CookieConsent />
          <TermsGate />
          <CartDrawer />
          <FloatingUtilityDock />
          </AdProvider>
          </CommandPaletteProvider>
          </FeatureFlagsProvider>
        </BrowserRouter>
        </CartProvider>
      </DesignSystemProvider>
      </HelmetProvider>
    </AuthContext.Provider>
  );
}

export default App;
